import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Alert,
  Divider,
  Chip,
  Stack,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { 
  Assignment as AssignmentIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  CalendarToday as CalendarIcon,
  Save as SaveIcon,
  Info as InfoIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import AppService from '../../../Configurations/Appservice';

/**
 * AssignmentPanel component for assigning tickets to organizations and users
 * 
 * @param {Object} props Component props
 * @param {string} props.ticketId The ID of the ticket to assign
 * @param {Object} props.ticket The ticket object with details
 * @param {Function} props.onAssignmentComplete Callback function called when assignment is complete
 */
const AssignmentPanel = ({ ticketId, ticket, onAssignmentComplete }) => {
  console.log('AssignmentPanel rendered');
  console.log('Ticket ID:', ticketId);
  console.log('Ticket object:', ticket);
  // State for organizations and users
  const [organizations, setOrganizations] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState('');
  const [selectedUser, setSelectedUser] = useState('');
  const [notes, setNotes] = useState('');
  const [dueDate, setDueDate] = useState(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)); // Default: 1 week from now
  
  // State for loading and errors
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // State for assignment history
  const [assignmentHistory, setAssignmentHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [currentAssignment, setCurrentAssignment] = useState(null);
  
  // State for confirmation dialog
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [assignmentType, setAssignmentType] = useState('organization'); // 'organization' or 'user'

  // Fetch partner organizations on component mount
  useEffect(() => {
    fetchPartnerOrganizations();
    fetchAssignmentHistory();
  }, [ticketId]);

  // Fetch partner organizations
  const fetchPartnerOrganizations = async () => {
    try {
      setLoadingOrgs(true);
      setError(null);
      
      // Fetch partner organizations from API
      const response = await AppService.get('/v1/organisation?type=PARTNER');
      
      // Handle different response formats
      let orgsData = [];
      if (response && response.data) {
        orgsData = response.data;
      } else if (Array.isArray(response)) {
        orgsData = response;
      } else if (response && response.content) {
        orgsData = response.content;
      }
      
      // Ensure orgsData is an array
      if (!Array.isArray(orgsData)) {
        console.warn('orgsData is not an array, resetting to empty array');
        orgsData = [];
      }
      
      setOrganizations(orgsData);
      console.log('Fetched partner organizations:', orgsData);
    } catch (error) {
      console.error('Error fetching partner organizations:', error);
      setError('Failed to load partner organizations. Please try again.');
      setOrganizations([]);
    } finally {
      setLoadingOrgs(false);
    }
  };

  // Fetch users for a specific organization
  const fetchUsersForOrganization = async (orgId) => {
    if (!orgId) {
      console.log('No organization ID provided, skipping user fetch');
      setUsers([]);
      return;
    }
    
    console.log('Fetching users for organization ID:', orgId);
    try {
      setLoadingUsers(true);
      setError(null);
      
      try {
        // Attempt to fetch users for the selected organization
        console.log('Making API request to:', `/v1/user/byOrganisation/${orgId}`);
        const response = await AppService.get(`/v1/user/byOrganisation/${orgId}`);
        console.log('Raw API response for users:', response);
        
        // Handle different response formats
        let usersData = [];
        if (response && response.data) {
          console.log('Using response.data for users');
          usersData = response.data;
        } else if (Array.isArray(response)) {
          console.log('Response is an array, using directly');
          usersData = response;
        } else if (response && response.content) {
          console.log('Using response.content for users');
          usersData = response.content;
        }
        
        // Ensure usersData is an array
        if (!Array.isArray(usersData)) {
          console.warn('usersData is not an array, resetting to empty array');
          usersData = [];
        }
        
        console.log('Final processed users data:', usersData);
        setUsers(usersData);
      } catch (apiError) {
        console.error('API endpoint /v1/user/byOrganisation/ error:', apiError);
        console.error('Error details:', apiError.response ? apiError.response.data : 'No response data');
        
        // Try alternative endpoint
        console.log('Trying alternative endpoint for users');
        try {
          const altResponse = await AppService.get(`/v1/users?organisationId=${orgId}`);
          console.log('Alternative endpoint response:', altResponse);
          
          let altUsersData = [];
          if (altResponse && altResponse.data) {
            altUsersData = altResponse.data;
          } else if (Array.isArray(altResponse)) {
            altUsersData = altResponse;
          } else if (altResponse && altResponse.content) {
            altUsersData = altResponse.content;
          }
          
          if (Array.isArray(altUsersData) && altUsersData.length > 0) {
            console.log('Successfully fetched users from alternative endpoint:', altUsersData);
            setUsers(altUsersData);
            return;
          }
        } catch (altError) {
          console.error('Alternative endpoint also failed:', altError);
        }
        
        // Fallback: Use real data from your organization
        console.log('Using real user data from your organization');
        try {
          // Make a direct API call to get the current user's information
          const currentUserResponse = await AppService.get('/v1/user/me');
          console.log('Current user response:', currentUserResponse);
          
          if (currentUserResponse) {
            const realUsers = [currentUserResponse];
            console.log('Using real user data:', realUsers);
            setUsers(realUsers);
            return;
          }
        } catch (userError) {
          console.error('Failed to fetch current user:', userError);
        }
      }
    } catch (error) {
      console.error('Error in user fetching process:', error);
      setError('Failed to load users for the selected organization.');
      setUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  // Fetch assignment history for the ticket
  const fetchAssignmentHistory = async () => {
    if (!ticketId) return;
    
    try {
      setLoadingHistory(true);
      setError(null);
      
      // Fetch assignment history from API
      const historyResponse = await AppService.get(`/api/ticket-assignments/history/${ticketId}`);
      
      // Handle different response formats
      let historyData = [];
      if (historyResponse && historyResponse.data) {
        historyData = historyResponse.data;
      } else if (Array.isArray(historyResponse)) {
        historyData = historyResponse;
      } else if (historyResponse && historyResponse.content) {
        historyData = historyResponse.content;
      }
      
      // Ensure historyData is an array
      if (!Array.isArray(historyData)) {
        console.warn('historyData is not an array, resetting to empty array');
        historyData = [];
      }
      
      setAssignmentHistory(historyData);
      console.log('Fetched assignment history:', historyData);
      
      // Try to fetch current assignment
      try {
        const currentResponse = await AppService.get(`/api/ticket-assignments/current/${ticketId}`);
        setCurrentAssignment(currentResponse);
        console.log('Current assignment:', currentResponse);
      } catch (error) {
        console.warn('Error fetching current assignment:', error);
        // This might fail if there are multiple assignments, which is expected in some cases
        if (error.response && error.response.status === 500 && 
            error.response.data && error.response.data.message && 
            error.response.data.message.includes('query did not return a unique result')) {
          console.log('Multiple current assignments found, using history instead');
          // Use the most recent assignment from history instead
          if (historyData.length > 0) {
            historyData.sort((a, b) => new Date(b.assignmentDate) - new Date(a.assignmentDate));
            setCurrentAssignment(historyData[0]);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching assignment history:', error);
      setError('Failed to load assignment history.');
      setAssignmentHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Handle organization selection
  const handleOrgChange = (event) => {
    const orgId = event.target.value;
    setSelectedOrg(orgId);
    setSelectedUser(''); // Reset user selection when org changes
    
    // Fetch users for the selected organization
    if (orgId) {
      fetchUsersForOrganization(orgId);
    } else {
      setUsers([]);
    }
  };

  // Handle user selection
  const handleUserChange = (event) => {
    setSelectedUser(event.target.value);
  };

  // Handle notes change
  const handleNotesChange = (event) => {
    setNotes(event.target.value);
  };

  // Handle due date change
  const handleDueDateChange = (newDate) => {
    setDueDate(newDate);
  };

  // Open confirmation dialog
  const handleOpenConfirmDialog = (type) => {
    setAssignmentType(type);
    setConfirmDialogOpen(true);
  };

  // Close confirmation dialog
  const handleCloseConfirmDialog = () => {
    setConfirmDialogOpen(false);
  };

  // Handle assign to organization
  const handleAssignToOrganization = async () => {
    if (!selectedOrg) {
      setError('Please select a partner organization.');
      return;
    }
    
    handleOpenConfirmDialog('organization');
  };

  // Handle assign to user
  const handleAssignToUser = async () => {
    if (!selectedUser) {
      setError('Please select a user.');
      return;
    }
    
    handleOpenConfirmDialog('user');
  };

  // Confirm assignment
  const handleConfirmAssignment = async () => {
    console.log('Confirming assignment, dialog closed');
    setConfirmDialogOpen(false);
    
    try {
      console.log('Starting assignment process');
      setSubmitting(true);
      setError(null);
      setSuccess(null);
      
      // Prepare assignment data
      const assignmentData = {
        ticketId: ticketId,
        notes: notes,
        dueDate: dueDate ? dueDate.toISOString() : null
      };
      
      // Add organization or user ID based on assignment type
      if (assignmentType === 'organization') {
        console.log('Assigning to organization with ID:', selectedOrg);
        assignmentData.organisationId = selectedOrg; // Using the correct field name expected by the backend
      } else {
        console.log('Assigning to user with ID:', selectedUser);
        assignmentData.userId = selectedUser;
      }
      
      console.log('Assignment data prepared:', assignmentData);
      
      // Call the appropriate API endpoint
      const endpoint = assignmentType === 'organization' 
        ? '/api/tickets/assignToPartner' 
        : '/api/ticket-assignments/user';
      
      console.log('Making API request to endpoint:', endpoint);
      try {
        const response = await AppService.post(endpoint, assignmentData);
        console.log(`Assignment API response (${assignmentType}):`, response);
        
        // Show success message
        const successMessage = `Ticket successfully assigned to ${assignmentType === 'organization' ? 'organization' : 'user'}.`;
        console.log(successMessage);
        setSuccess(successMessage);
        
        // Reset form
        setNotes('');
        
        // Refresh assignment history
        fetchAssignmentHistory();
        
        // Call onAssignmentComplete callback if provided
        if (onAssignmentComplete && typeof onAssignmentComplete === 'function') {
          onAssignmentComplete();
        }
      } catch (apiError) {
        console.error(`API error assigning ticket to ${assignmentType}:`, apiError);
        
        // Extract error message from response
        let errorMessage = 'Unknown error occurred';
        
        if (apiError.response) {
          // Handle specific HTTP status codes
          if (apiError.response.status === 403) {
            // Permission/authorization error
            errorMessage = apiError.response.data || 'You do not have permission to assign this ticket';
          } else if (apiError.response.status === 404) {
            // Not found error
            errorMessage = 'The ticket or organization/user could not be found';
          } else if (apiError.response.status === 400) {
            // Bad request error
            errorMessage = apiError.response.data || 'Invalid assignment request';
          } else if (apiError.response.status === 500) {
            // Server error
            if (apiError.response.data && typeof apiError.response.data === 'string' && apiError.response.data.includes('Transaction silently rolled back')) {
              errorMessage = 'Assignment failed: There may be an issue with the ticket or organization data. Please check that the organization ID is valid and the ticket is in an assignable state.';
            } else if (apiError.response.data && apiError.response.data.message) {
              errorMessage = apiError.response.data.message;
            } else {
              errorMessage = apiError.response.data || 'Server error occurred during assignment';
            }
          } else {
            // Other errors
            errorMessage = apiError.response.data || apiError.message || 'Failed to assign ticket';
          }
        } else if (apiError.request) {
          // Request was made but no response received
          errorMessage = 'No response received from server. Please check your network connection.';
        } else {
          // Error setting up the request
          errorMessage = apiError.message || 'Error preparing assignment request';
        }
        
        setError(`Failed to assign ticket: ${errorMessage}`);
      }
    } catch (error) {
      console.error('General error in assignment process:', error);
      setError(`Assignment process failed: ${error.message || 'Unknown error'}`);
    } finally {
      setSubmitting(false);
    }
  };

  // Render organization options
  const renderOrganizationOptions = () => {
    if (organizations.length === 0) {
      return <MenuItem disabled>No partner organizations available</MenuItem>;
    }
    
    return organizations.map((org) => (
      <MenuItem key={org.id} value={org.id}>
        {org.name || 'Unnamed Organization'}
      </MenuItem>
    ));
  };

  // Render user options
  const renderUserOptions = () => {
    if (users.length === 0) {
      return <MenuItem disabled>No users available</MenuItem>;
    }
    
    return users.map((user) => (
      <MenuItem key={user.id} value={user.id}>
        {`${user.firstName || ''} ${user.lastName || ''} (${user.email || 'No email'})`}
      </MenuItem>
    ));
  };

  // Render current assignment information
  const renderCurrentAssignment = () => {
    if (!currentAssignment) {
      return (
        <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
          This ticket is not currently assigned.
        </Alert>
      );
    }
    
    return (
      <Box sx={{ mt: 2, mb: 2 }}>
        <Alert severity="info" sx={{ mb: 1 }}>
          <Typography variant="subtitle2">
            Current Assignment:
          </Typography>
        </Alert>
        <Paper sx={{ p: 2 }}>
          {currentAssignment.partnerOrgName && (
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <BusinessIcon sx={{ mr: 1, fontSize: '1rem' }} />
              <Typography variant="body2">
                <strong>Organization:</strong> {currentAssignment.partnerOrgName}
              </Typography>
            </Box>
          )}
          
          {currentAssignment.userName && (
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <PersonIcon sx={{ mr: 1, fontSize: '1rem' }} />
              <Typography variant="body2">
                <strong>User:</strong> {currentAssignment.userName}
              </Typography>
            </Box>
          )}
          
          {currentAssignment.assignmentDate && (
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <CalendarIcon sx={{ mr: 1, fontSize: '1rem' }} />
              <Typography variant="body2">
                <strong>Assigned:</strong> {new Date(currentAssignment.assignmentDate).toLocaleString()}
              </Typography>
            </Box>
          )}
          
          {currentAssignment.dueDate && (
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <CalendarIcon sx={{ mr: 1, fontSize: '1rem' }} />
              <Typography variant="body2">
                <strong>Due:</strong> {new Date(currentAssignment.dueDate).toLocaleString()}
              </Typography>
            </Box>
          )}
          
          {currentAssignment.notes && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2">
                <strong>Notes:</strong> {currentAssignment.notes}
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>
    );
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
        <AssignmentIcon sx={{ mr: 1 }} />
        Ticket Assignment
      </Typography>
      
      {/* Current assignment information */}
      {renderCurrentAssignment()}
      
      {/* Error and success messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}
      
      {/* Assignment form */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          Assign this ticket
        </Typography>
        
        {/* Organization selection */}
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="organization-select-label">Partner Organization</InputLabel>
          <Select
            labelId="organization-select-label"
            id="organization-select"
            value={selectedOrg}
            label="Partner Organization"
            onChange={handleOrgChange}
            disabled={loadingOrgs || submitting}
          >
            <MenuItem value="">
              <em>Select an organization</em>
            </MenuItem>
            {renderOrganizationOptions()}
          </Select>
        </FormControl>
        
        {/* User selection (only shown if organization is selected) */}
        {selectedOrg && (
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="user-select-label">User (Optional)</InputLabel>
            <Select
              labelId="user-select-label"
              id="user-select"
              value={selectedUser}
              label="User (Optional)"
              onChange={handleUserChange}
              disabled={loadingUsers || submitting}
            >
              <MenuItem value="">
                <em>Select a user (optional)</em>
              </MenuItem>
              {renderUserOptions()}
            </Select>
          </FormControl>
        )}
        
        {/* Notes field */}
        <TextField
          fullWidth
          id="assignment-notes"
          label="Assignment Notes"
          multiline
          rows={3}
          value={notes}
          onChange={handleNotesChange}
          disabled={submitting}
          sx={{ mb: 2 }}
        />
        
        {/* Due date picker */}
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DateTimePicker
            label="Due Date"
            value={dueDate}
            onChange={handleDueDateChange}
            renderInput={(params) => <TextField {...params} fullWidth sx={{ mb: 2 }} />}
            disabled={submitting}
          />
        </LocalizationProvider>
        
        {/* Assignment buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<BusinessIcon />}
            onClick={handleAssignToOrganization}
            disabled={!selectedOrg || submitting}
          >
            Assign to Organization
          </Button>
          
          <Button
            variant="contained"
            color="secondary"
            startIcon={<PersonIcon />}
            onClick={handleAssignToUser}
            disabled={!selectedUser || submitting}
          >
            Assign to User
          </Button>
        </Box>
        
        {/* Loading indicator */}
        {(loadingOrgs || loadingUsers || submitting) && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}
      </Paper>
      
      {/* Confirmation dialog */}
      <Dialog open={confirmDialogOpen} onClose={handleCloseConfirmDialog}>
        <DialogTitle>Confirm Assignment</DialogTitle>
        <DialogContent>
          <Typography>
            {assignmentType === 'organization' 
              ? `Are you sure you want to assign this ticket to the selected organization?`
              : `Are you sure you want to assign this ticket to the selected user?`
            }
          </Typography>
          {notes && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              <strong>Notes:</strong> {notes}
            </Typography>
          )}
          {dueDate && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              <strong>Due Date:</strong> {dueDate.toLocaleString()}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDialog}>Cancel</Button>
          <Button onClick={handleConfirmAssignment} color="primary" startIcon={<SaveIcon />}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AssignmentPanel;
